/**
 * stores/auth.ts — Authentication state
 *
 * Manages auth method (email or web3), session token,
 * and authenticated user address/email.
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type AuthMethod = 'email' | 'web3' | null;

/** localStorage key for persisting session */
const STORAGE_KEY = 'localusdt_session';

interface PersistedSession {
    method: AuthMethod;
    email: string | null;
    walletAddress: string | null;
    token: string;
    role: string;
    isNewUser: boolean;
    expiresAt: string;
}

export const useAuthStore = defineStore('auth', () => {
    /* -------------------------------------------------- */
    /* State                                               */
    /* -------------------------------------------------- */
    const method = ref<AuthMethod>(null);
    const email = ref<string | null>(null);
    const walletAddress = ref<string | null>(null);
    const sessionToken = ref<string | null>(null);
    const role = ref<string | null>(null);
    const isNewUser = ref(false);
    const expiresAt = ref<string | null>(null);

    /* -------------------------------------------------- */
    /* Getters                                             */
    /* -------------------------------------------------- */
    const isAuthenticated = computed(() => sessionToken.value !== null);

    const displayIdentifier = computed(() => {
        if (method.value === 'email' && email.value) {
            return email.value;
        }
        if (method.value === 'web3' && walletAddress.value) {
            /** Show truncated address: 0x1234...5678 */
            const addr = walletAddress.value;
            return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
        }
        return null;
    });

    /* -------------------------------------------------- */
    /* Actions                                             */
    /* -------------------------------------------------- */

    /**
     * Set auth state after successful email OTP verification.
     */
    function loginWithEmail(
        userEmail: string,
        token: string,
        userRole: string = 'user',
        newUser: boolean = false,
        userExpiresAt?: string,
    ): void {
        method.value = 'email';
        email.value = userEmail;
        walletAddress.value = null;
        sessionToken.value = token;
        role.value = userRole;
        isNewUser.value = newUser;
        expiresAt.value = userExpiresAt ?? null;

        /** Persist to localStorage for page refreshes */
        const session: PersistedSession = {
            method: 'email',
            email: userEmail,
            walletAddress: null,
            token,
            role: userRole,
            isNewUser: newUser,
            expiresAt: userExpiresAt ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

        /** Legacy flag for router guard compatibility */
        localStorage.setItem('localusdt_auth', 'true');

        /** Debug: log email auth */
        console.log(`[AUTH] Logged in via email: ${userEmail} (newUser: ${newUser})`);
    }

    /**
     * Set auth state after successful Web3 wallet signature.
     */
    function loginWithWeb3(
        address: string,
        token: string,
        userRole: string = 'user',
        newUser: boolean = false,
        userExpiresAt?: string,
    ): void {
        method.value = 'web3';
        walletAddress.value = address.toLowerCase();
        email.value = null;
        sessionToken.value = token;
        role.value = userRole;
        isNewUser.value = newUser;
        expiresAt.value = userExpiresAt ?? null;

        /** Persist to localStorage for page refreshes */
        const session: PersistedSession = {
            method: 'web3',
            email: null,
            walletAddress: address.toLowerCase(),
            token,
            role: userRole,
            isNewUser: newUser,
            expiresAt: userExpiresAt ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

        /** Legacy flag for router guard compatibility */
        localStorage.setItem('localusdt_auth', 'true');

        /** Debug: log web3 auth */
        console.log(`[AUTH] Logged in via web3: ${address} (newUser: ${newUser})`);
    }

    /**
     * Mark onboarding as complete (user finished profile setup).
     */
    function completeOnboarding(): void {
        isNewUser.value = false;

        /** Update persisted session */
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            try {
                const session: PersistedSession = JSON.parse(raw);
                session.isNewUser = false;
                localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
            } catch {
                /** Ignore parse errors */
            }
        }

        localStorage.setItem('localusdt_profile', 'true');

        /** Debug: log onboarding complete */
        console.log('[AUTH] Onboarding completed');
    }

    /**
     * Clear all auth state and redirect to login.
     */
    function logout(): void {
        /** Debug: log logout */
        console.log('[AUTH] Logging out');

        method.value = null;
        email.value = null;
        walletAddress.value = null;
        sessionToken.value = null;
        role.value = null;
        isNewUser.value = false;
        expiresAt.value = null;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('localusdt_auth');
        localStorage.removeItem('localusdt_profile');
    }

    /**
     * Restore session from localStorage on app init.
     * Returns true if a valid session was restored.
     */
    function restore(): boolean {
        const raw = localStorage.getItem(STORAGE_KEY);

        if (!raw) {
            console.log('[AUTH] No persisted session found');
            return false;
        }

        try {
            const session: PersistedSession = JSON.parse(raw);

            /** Check if session has expired client-side */
            if (new Date(session.expiresAt) < new Date()) {
                console.log('[AUTH] Persisted session expired — clearing');
                logout();
                return false;
            }

            method.value = session.method;
            email.value = session.email;
            walletAddress.value = session.walletAddress;
            sessionToken.value = session.token;
            role.value = session.role;
            isNewUser.value = session.isNewUser;
            expiresAt.value = session.expiresAt;

            /** Legacy flags for router guard compatibility */
            localStorage.setItem('localusdt_auth', 'true');
            if (!session.isNewUser) {
                localStorage.setItem('localusdt_profile', 'true');
            }

            /** Debug: log session restore */
            console.log(`[AUTH] Session restored: ${session.email ?? session.walletAddress} (expires: ${session.expiresAt})`);

            return true;
        } catch (err) {
            console.error('[AUTH] Failed to parse persisted session:', err);
            logout();
            return false;
        }
    }

    return {
        method,
        email,
        walletAddress,
        sessionToken,
        role,
        isNewUser,
        expiresAt,
        isAuthenticated,
        displayIdentifier,
        loginWithEmail,
        loginWithWeb3,
        completeOnboarding,
        logout,
        restore,
    };
});
